import React, { useMemo, Fragment } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import Tooltip from '@material-ui/core/Tooltip';
import TooltipV5 from '@mui/material/Tooltip';
import HelpIcon from '@material-ui/icons/HelpSharp';
import { getRTLPopperStyles } from '~/modules/common/util';

const useStyles = makeStyles(theme => ({
  title: {
    margin: 0,
    '$message + &': {
      marginTop: theme.spacing(1)
    }
  },
  message: {
    margin: 0,
    '& + &': {
      marginTop: theme.spacing(1)
    }
  },
  helpIcon: {
    color: theme.palette.text.secondary,
    cursor: 'help',
    opacity: 0.3,
    verticalAlign: 'middle',
    transition: theme.transitions.create(['opacity'], {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.shortest
    }),
    '&:hover': {
      opacity: 1
    }
  }
}));

const useTooltipStyles = makeStyles(theme => ({
  popper: ({ curr }) => getRTLPopperStyles({ curr, theme })
}));

export const SummaryCardHelp = ({
  content,
  classes: classesOverride,
  tooltipVariant = 'v4',
  iconAriaLabel,
  tooltipDescriptionId
}) => {
  const classes = useStyles({ classes: classesOverride });
  const tooltipPopperClasses = useTooltipStyles();

  const tooltipContent = useMemo(
    () => <Content classes={classes} content={content} />,
    [classes, content]
  );

  const TooltipComponent = tooltipVariant === 'v5' ? TooltipV5 : Tooltip;

  return (
    <TooltipComponent
      title={tooltipContent}
      enterTouchDelay={0}
      classes={tooltipPopperClasses}
      {...(tooltipVariant === 'v4' && { interactive: true })}
      {...(tooltipVariant === 'v5' &&
        tooltipDescriptionId && { id: tooltipDescriptionId })}
    >
      <HelpIcon
        className={classes.helpIcon}
        tabIndex={0}
        {...(tooltipVariant === 'v5' && {
          role: 'img',
          'aria-label': iconAriaLabel,
          ...(tooltipDescriptionId && {
            'aria-describedby': tooltipDescriptionId
          })
        })}
      />
    </TooltipComponent>
  );
};

const Content = ({ classes, content }) => {
  return (
    <>
      {content.map(item => (
        <Fragment key={item.title}>
          {item.title ? <h4 className={classes.title}>{item.title}</h4> : null}
          <p className={classes.message}>{item.message}</p>
        </Fragment>
      ))}
    </>
  );
};

SummaryCardHelp.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.node,
      message: PropTypes.node.isRequired
    }).isRequired
  ).isRequired,
  tooltipVariant: PropTypes.oneOf(['v4', 'v5']),
  iconAriaLabel: PropTypes.string,
  tooltipDescriptionId: PropTypes.string,
  classes: PropTypes.object
};

Content.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.node,
      message: PropTypes.node.isRequired
    }).isRequired
  ).isRequired,
  classes: PropTypes.object
};

export default SummaryCardHelp;
